/**
 * The rules of one Scrabble play — a line-for-line port of
 * `backend/apps/puzzletypes/scrabble/board.py`, proven equal by the shared fixtures.
 *
 * Pure geometry and arithmetic: where a word may go, which tiles it takes from the rack,
 * which words it forms, and what they score. Whether the words exist is the server's
 * business — the dictionary never reaches the browser (ADR 0040).
 */
import {
  BINGO_BONUS,
  BLANK,
  CENTRE,
  EMPTY,
  LETTER_VALUES,
  PREMIUM_ROWS,
  RACK_MAX,
  SIZE,
  cellKey,
  type Cell,
  type Direction,
  type Language,
  type PlacedTile,
  type Premium,
  type ScrabbleDefinition,
  type ScrabblePlay,
  type ScrabbleProgress,
} from "./types";

export interface Tile {
  /** Always uppercase. */
  letter: string;
  /** A blank played as `letter`: worth nothing. */
  blank: boolean;
}

/** Keyed by `r,c`. */
export type Board = Map<string, Tile>;
/** Tiles and blanks, counted. */
export type Rack = Map<string, number>;

export interface Placement {
  cell: Cell;
  tile: Tile;
}

export interface WordScore {
  word: string;
  cells: Cell[];
  points: number;
}

export interface PlayOutcome {
  legal: boolean;
  /** Feedback code when illegal. */
  code: string;
  /** Finer grain for `illegal_placement`. */
  reason: string;
  /** What to highlight when illegal. */
  cells: Cell[];
  placed: Placement[];
  /** The main word first, then one per cross-word. */
  words: WordScore[];
  points: number;
  bingo: boolean;
}

export class BoardError extends Error {}

const PREMIUM_NAMES: Record<string, Premium> = { T: "TW", D: "DW", t: "TL", d: "DL" };

function illegal(code: string, reason = "", cells: Cell[] = []): PlayOutcome {
  return { legal: false, code, reason, cells, placed: [], words: [], points: 0, bingo: false };
}

/** `.` empty, `A-Z` a tile, `a-z` a blank already played as that letter. */
export function parseBoard(rows: string[]): Board {
  if (rows.length !== SIZE) throw new BoardError(`expected ${SIZE} rows, got ${rows.length}`);
  const board: Board = new Map();
  rows.forEach((row, r) => {
    if (row.length !== SIZE) throw new BoardError(`row ${r} has ${row.length} cells`);
    Array.from(row).forEach((char, c) => {
      if (char === EMPTY) return;
      if (!/^[A-Za-z]$/.test(char)) throw new BoardError(`unexpected ${char} at ${r},${c}`);
      board.set(cellKey([r, c]), {
        letter: char.toUpperCase(),
        blank: char !== char.toUpperCase(),
      });
    });
  });
  return board;
}

/** Letters and `?` blanks, counted. Case-insensitive. */
export function parseRack(rack: string): Rack {
  const counts: Rack = new Map();
  let total = 0;
  for (const char of rack) {
    const key = char === BLANK ? BLANK : /^[A-Za-z]$/.test(char) ? char.toUpperCase() : null;
    if (key === null) throw new BoardError(`unexpected ${char} in the rack`);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    total += 1;
  }
  if (total > RACK_MAX) throw new BoardError(`more than ${RACK_MAX} tiles in the rack`);
  return counts;
}

export function premium([r, c]: Cell): Premium {
  return PREMIUM_NAMES[PREMIUM_ROWS[r]?.[c] ?? ""] ?? "";
}

export function letterValue(language: Language, tile: Tile): number {
  return tile.blank ? 0 : (LETTER_VALUES[language][tile.letter] ?? 0);
}

export function onBoard([r, c]: Cell): boolean {
  return r >= 0 && r < SIZE && c >= 0 && c < SIZE;
}

export function step(direction: Direction): Cell {
  return direction === "across" ? [0, 1] : [1, 0];
}

export function neighbours([r, c]: Cell): Cell[] {
  return (
    [
      [r - 1, c],
      [r + 1, c],
      [r, c - 1],
      [r, c + 1],
    ] as Cell[]
  ).filter(onBoard);
}

/** The maximal run of occupied cells through `cell` (itself occupied) along `direction`. */
export function runThrough(board: Board, cell: Cell, direction: Direction): Cell[] {
  const [dr, dc] = step(direction);
  let start = cell;
  for (;;) {
    const prev: Cell = [start[0] - dr, start[1] - dc];
    if (!onBoard(prev) || !board.has(cellKey(prev))) break;
    start = prev;
  }
  const cells: Cell[] = [];
  let cur = start;
  while (onBoard(cur) && board.has(cellKey(cur))) {
    cells.push(cur);
    cur = [cur[0] + dr, cur[1] + dc];
  }
  return cells;
}

export function wordOf(board: Board, cells: Cell[]): string {
  return cells.map((cell) => board.get(cellKey(cell))?.letter ?? "").join("");
}

function parseKey(key: string): Cell {
  const [r, c] = key.split(",").map(Number);
  return [r, c];
}

/** How many islands of tiles the board holds; a playable board has at most one. */
export function components(board: Board): number {
  const seen = new Set<string>();
  let count = 0;
  for (const start of board.keys()) {
    if (seen.has(start)) continue;
    count += 1;
    const queue = [start];
    seen.add(start);
    while (queue.length > 0) {
      const key = queue.shift() as string;
      for (const next of neighbours(parseKey(key))) {
        const nextKey = cellKey(next);
        if (board.has(nextKey) && !seen.has(nextKey)) {
          seen.add(nextKey);
          queue.push(nextKey);
        }
      }
    }
  }
  return count;
}

/** Every run of two or more tiles, across then down, in reading order. */
export function wordsOnBoard(board: Board): { word: string; cells: Cell[] }[] {
  const words: { word: string; cells: Cell[] }[] = [];
  const sorted = [...board.keys()].map(parseKey).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  for (const direction of ["across", "down"] as Direction[]) {
    const [dr, dc] = step(direction);
    for (const cell of sorted) {
      const prev: Cell = [cell[0] - dr, cell[1] - dc];
      if (onBoard(prev) && board.has(cellKey(prev))) continue;
      const cells = runThrough(board, cell, direction);
      if (cells.length >= 2) words.push({ word: wordOf(board, cells), cells });
    }
  }
  return words;
}

/** Premiums count only under the tiles placed this turn. */
function scoreWord(board: Board, language: Language, cells: Cell[], fresh: Set<string>): number {
  let total = 0;
  let multiplier = 1;
  for (const cell of cells) {
    const tile = board.get(cellKey(cell)) as Tile;
    let value = letterValue(language, tile);
    if (fresh.has(cellKey(cell))) {
      const bonus = premium(cell);
      if (bonus === "DL") value *= 2;
      else if (bonus === "TL") value *= 3;
      else if (bonus === "DW") multiplier *= 2;
      else if (bonus === "TW") multiplier *= 3;
    }
    total += value;
  }
  return total * multiplier;
}

/** Legality and score of `play` on `board` with `rack`. Dictionary not consulted. */
export function resolvePlay(
  board: Board,
  rack: Rack,
  language: Language,
  play: ScrabblePlay,
): PlayOutcome {
  const [dr, dc] = step(play.direction);
  const start: Cell = [play.row, play.col];
  const length = play.word.length;
  const end: Cell = [start[0] + dr * (length - 1), start[1] + dc * (length - 1)];
  if (!onBoard(start) || !onBoard(end)) return illegal("illegal_placement", "off_board");
  const span: Cell[] = Array.from({ length }, (_, i) => [start[0] + dr * i, start[1] + dc * i]);
  const before: Cell = [start[0] - dr, start[1] - dc];
  const after: Cell = [end[0] + dr, end[1] + dc];
  if (
    (onBoard(before) && board.has(cellKey(before))) ||
    (onBoard(after) && board.has(cellKey(after)))
  )
    // The submitted word stops short of the tiles it would actually run into.
    return illegal("illegal_placement", "word_not_complete", span);

  const placed: Placement[] = [];
  const needed: Rack = new Map();
  for (let i = 0; i < length; i += 1) {
    const cell = span[i];
    const letter = play.word[i];
    const existing = board.get(cellKey(cell));
    if (existing) {
      if (existing.letter !== letter.toUpperCase())
        return illegal("illegal_placement", "letter_mismatch", [cell]);
      continue;
    }
    const tile: Tile = { letter: letter.toUpperCase(), blank: letter !== letter.toUpperCase() };
    placed.push({ cell, tile });
    const key = tile.blank ? BLANK : tile.letter;
    needed.set(key, (needed.get(key) ?? 0) + 1);
  }
  if (placed.length === 0) return illegal("illegal_placement", "no_tile_placed", span);

  const missing = new Set<string>();
  for (const [key, count] of needed) if (count > (rack.get(key) ?? 0)) missing.add(key);
  if (missing.size > 0) {
    const short = placed
      .filter((p) => missing.has(p.tile.blank ? BLANK : p.tile.letter))
      .map((p) => p.cell);
    return illegal("tiles_not_in_rack", "", short);
  }

  const fresh = new Set(placed.map((p) => cellKey(p.cell)));
  const connected =
    board.size === 0
      ? fresh.has(cellKey(CENTRE))
      : span.some((cell) => board.has(cellKey(cell))) ||
        placed.some((p) => neighbours(p.cell).some((n) => board.has(cellKey(n))));
  if (!connected)
    return illegal(
      "not_connected",
      "",
      placed.map((p) => p.cell),
    );

  const merged: Board = new Map(board);
  for (const p of placed) merged.set(cellKey(p.cell), p.tile);
  const words: WordScore[] = [
    { word: wordOf(merged, span), cells: span, points: scoreWord(merged, language, span, fresh) },
  ];
  const cross: Direction = play.direction === "across" ? "down" : "across";
  for (const p of placed) {
    const cells = runThrough(merged, p.cell, cross);
    if (cells.length >= 2)
      words.push({
        word: wordOf(merged, cells),
        cells,
        points: scoreWord(merged, language, cells, fresh),
      });
  }
  const bingo = placed.length === RACK_MAX;
  const points = words.reduce((sum, w) => sum + w.points, 0) + (bingo ? BINGO_BONUS : 0);
  return { legal: true, code: "", reason: "", cells: [], placed, words, points, bingo };
}

// ---- Client-side only: from tentative tiles to a play -------------------------------

/**
 * The play the placed tiles amount to, or null when they do not line up: all on one row or
 * one column, contiguous once the board's own tiles fill the gaps, extended to the full run.
 * A lone tile reads across when that run is a word, else down.
 */
export function playFromTiles(board: Board, placed: PlacedTile[]): ScrabblePlay | null {
  if (placed.length === 0) return null;
  const rows = new Set(placed.map((p) => p.r));
  const cols = new Set(placed.map((p) => p.c));
  let direction: Direction;
  if (rows.size === 1 && cols.size === 1) {
    const cell: Cell = [placed[0].r, placed[0].c];
    const merged = withTiles(board, placed);
    direction = runThrough(merged, cell, "across").length >= 2 ? "across" : "down";
  } else if (rows.size === 1) direction = "across";
  else if (cols.size === 1) direction = "down";
  else return null;
  const merged = withTiles(board, placed);
  const first: Cell = [placed[0].r, placed[0].c];
  const run = runThrough(merged, first, direction);
  const inRun = new Set(run.map(cellKey));
  if (!placed.every((p) => inRun.has(cellKey([p.r, p.c])))) return null; // a gap
  const fresh = new Map(placed.map((p) => [cellKey([p.r, p.c]), p]));
  const word = run
    .map((cell) => {
      const own = fresh.get(cellKey(cell));
      if (own) return own.blank ? own.letter.toLowerCase() : own.letter;
      return merged.get(cellKey(cell))?.letter ?? "";
    })
    .join("");
  return { row: run[0][0], col: run[0][1], direction, word };
}

export function withTiles(board: Board, placed: PlacedTile[]): Board {
  const merged: Board = new Map(board);
  for (const p of placed) merged.set(cellKey([p.r, p.c]), { letter: p.letter, blank: p.blank });
  return merged;
}

/**
 * What the tentative tiles would score, or why they cannot be played yet — the same rules
 * the server applies, minus the dictionary. Null when nothing is placed.
 */
export function previewPlay(
  definition: ScrabbleDefinition,
  progress: ScrabbleProgress,
): PlayOutcome | null {
  if (progress.placed.length === 0) return null;
  let board: Board;
  let rack: Rack;
  try {
    board = parseBoard(definition.board);
    rack = parseRack(definition.rack);
  } catch {
    return illegal("illegal_placement", "bad_board");
  }
  const play = playFromTiles(board, progress.placed);
  if (!play)
    return illegal(
      "illegal_placement",
      "not_in_line",
      progress.placed.map((p) => [p.r, p.c] as Cell),
    );
  const outcome = resolvePlay(board, rack, definition.language, play);
  // A lone first tile on the star: legal by every rule above, but not yet a word.
  if (outcome.legal && play.word.length < 2)
    return illegal("illegal_placement", "word_too_short", [[play.row, play.col]]);
  return outcome;
}
